/**
 * Base URL de la API de reservas (sin barra final).
 * - `REACT_APP_NETWORKING_API_URL`: prioridad absoluta.
 * - Si el front corre en **localhost / 127.0.0.1**, se fuerza base vacía (proxy CRA → backend local) aunque
 *   exista `REACT_APP_API_URL` a producción, para evitar "Failed to fetch" por CORS o rutas que sirven el SPA.
 * - Si no es localhost y existe `REACT_APP_API_URL`, se usa `{origen}/networking-api` (misma cookie que `/api`).
 */
export function getNetworkingApiBase(): string {
  const explicit = process.env.REACT_APP_NETWORKING_API_URL;
  if (explicit && String(explicit).trim()) return String(explicit).trim().replace(/\/$/, '');

  // Front en localhost: siempre mismo origen + proxy → backend local (evita CORS y respuestas HTML del SPA
  // en producción cuando REACT_APP_API_URL apunta al dominio desplegado).
  if (typeof window !== 'undefined') {
    const h = window.location.hostname;
    if (h === 'localhost' || h === '127.0.0.1') {
      return '';
    }
  }

  const api = process.env.REACT_APP_API_URL;
  if (api && String(api).trim()) {
    let origin = String(api).trim().replace(/\/+$/, '');
    if (origin.endsWith('/api')) origin = origin.slice(0, -4);
    return `${origin}/networking-api`;
  }

  return '';
}

/** Código devuelto por POST /api/bookings/create cuando faltan datos obligatorios del perfil personal. */
export const NETWORKING_PROFILE_INCOMPLETE_CODE = 'NETWORKING_PROFILE_INCOMPLETE';

/** Código cuando el usuario ya tiene el máximo de reuniones reservadas. */
export const NETWORKING_BOOKING_LIMIT_REACHED_CODE = 'NETWORKING_BOOKING_LIMIT_REACHED';

export class NetworkingApiError extends Error {
  readonly code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = 'NetworkingApiError';
    this.code = code;
    Object.setPrototypeOf(this, NetworkingApiError.prototype);
  }
}

async function apiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const base = getNetworkingApiBase();
  const pathPart = path.startsWith('/') ? path : `/${path}`;
  const url = base ? `${base}${pathPart}` : `/networking-api${pathPart}`;

  const res = await fetch(url, { credentials: 'include', ...init });

  const ct = res.headers.get('content-type') || '';
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const fromBody = (body as { error?: string; details?: string }).error;
    const details = (body as { details?: string }).details;
    const code =
      typeof (body as { code?: string }).code === 'string'
        ? (body as { code: string }).code
        : undefined;
    const msg =
      fromBody ||
      (res.status === 504
        ? 'El servidor no respondió a tiempo. Comprobá que el backend de Perfil esté en marcha.'
        : res.statusText);
    throw new NetworkingApiError(msg, code);
  }
  return body as T;
}

export type ConfigResponse = {
  success: boolean;
  data: { eventDates: string[]; slotCapacity: number; maxBookingsPerUser: number };
};

export type MyBookingsMeta = {
  maxBookingsPerUser: number;
  bookingCount: number;
  canBookMore: boolean;
};

export async function fetchNetworkingConfig() {
  return apiRequest<ConfigResponse>('/api/config');
}

export type MyBookingRow = {
  id: number;
  slot_start: string;
  slot_end: string;
  partner_type: string;
  manual_name: string | null;
  manual_email: string | null;
  manual_company: string | null;
  /** JSON array de contrapartes manuales (hasta 5); ausente en reservas antiguas */
  manual_contacts_json?: string | null;
  initiative_owner_user_id?: number | null;
  initiative_client_id?: string | null;
  initiative_name_snapshot?: string | null;
  initiative_org_snapshot?: string | null;
};

export async function fetchMyBookingsMine() {
  return apiRequest<{ success: boolean; data: MyBookingRow[]; meta?: MyBookingsMeta }>(
    '/api/bookings/mine'
  );
}

export type SlotRow = {
  slotStart: string;
  slotEnd: string;
  booked: boolean;
  /** Máximo de reservas por misma franja (p. ej. 12). */
  capacity: number;
  freeSpots: number;
  bookingUserId?: number | null;
  isMine?: boolean;
  bookingId?: number | null;
  partnerType?: string | null;
};

export async function fetchNetworkingSchedule(date: string) {
  return apiRequest<{ success: boolean; data: SlotRow[] }>(`/api/schedule?date=${encodeURIComponent(date)}`);
}

export type InitiativeCatalogRow = {
  owner_user_id: number;
  initiative_client_id: string;
  name: string;
  short_description: string | null;
  type: string | null;
  type_label: string | null;
  organization_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  contact_first_name: string | null;
  contact_last_name: string | null;
  contact_position: string | null;
  /** Documentos de apoyo: enlace firmado hacia el proxy de descarga (sin exponer URL de storage). */
  support_documents?: Array<{ name: string; download_token: string }> | null;
};

/** URL para descargar un documento de apoyo del catálogo (GET con `Content-Disposition: attachment`). */
export function getCatalogSupportDownloadUrl(downloadToken: string): string {
  const base = getNetworkingApiBase();
  const path = `/api/catalog/support-file?token=${encodeURIComponent(downloadToken)}`;
  return base ? `${base}${path}` : `/networking-api${path}`;
}

export async function fetchNetworkingCatalog(q: string) {
  return apiRequest<{ success: boolean; data: InitiativeCatalogRow[] }>(
    `/api/catalog?q=${encodeURIComponent(q)}`
  );
}

export type CreateBookingPayload = Record<string, unknown>;

export async function createNetworkingBooking(payload: CreateBookingPayload) {
  return apiRequest<{ success: boolean; data: { id: number; manageToken: string } }>(
    '/api/bookings/create',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
}

export async function cancelNetworkingBooking(id: number) {
  return apiRequest<{ success: boolean }>(`/api/bookings/${id}/cancel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
}

export type UpdateBookingPartnerPayload = {
  partnerType: 'manual' | 'none';
  manualContacts?: Array<{
    fullName: string;
    email: string;
    phone: string;
    organization: string;
  }>;
};

export async function updateNetworkingBookingPartner(id: number, body: UpdateBookingPartnerPayload) {
  return apiRequest<{ success: boolean }>(`/api/bookings/${id}/update-partner`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export type BusinessRoundInterestedParticipant = {
  /** Clave única en el listado (usuario, iniciativa del catálogo o participante JSON). */
  entryId: string;
  /** Presente solo cuando el contacto corresponde a un usuario del sistema. */
  userId?: number | null;
  firstName: string;
  lastName: string;
  fullName: string;
  organization: string;
  organizationType: string;
  position: string;
  email: string;
  phone: string;
  area: string;
  subarea: string;
  profileKind?: 'personal' | 'institutional' | 'mixed';
  /** Si true, el modal no muestra correo ni teléfono (p. ej. institucional en borrador). */
  hideContact?: boolean;
  /** Orígenes del listado (p. ej. catalog_initiative, credenciales_web). */
  sources?: string[];
};

export type AddBusinessRoundParticipantResult =
  | { ok: true; fullName: string; counterpartyNumber: number }
  | { ok: false; reason: 'limit' };

export type BusinessRoundParticipantsMeta = {
  total: number;
  withoutContact: number;
  withContact: number;
};

export async function fetchBusinessRoundInterestedParticipants() {
  return apiRequest<{
    success: boolean;
    data: BusinessRoundInterestedParticipant[];
    meta?: BusinessRoundParticipantsMeta;
  }>('/api/participants');
}
